import { useCallback, useEffect, useRef, useState } from "react";
import { foyerApi } from "../http/foyer";

/**
 * useStaffPush - the browser half of the staff health-alert push channel.
 *
 * THE PROMPT ONLY EVER FIRES FROM A GESTURE. `Notification.requestPermission()`
 * is gesture-gated in every current browser, and - the expensive part - a
 * DENIED permission is sticky per origin: an operator who dismisses a prompt
 * they did not ask for cannot be prompted again, and has to go digging in site
 * settings to undo it. So this hook does nothing on mount but READ state, and
 * `enable()` is only ever reachable from a click.
 *
 * NOTHING IS REGISTERED UNTIL SOMEBODY OPTS IN either. On mount we call
 * `getRegistration`, not `register` - a console nobody wants alerts on never
 * acquires a service worker at all. `enable()` registers it, and only then.
 *
 * BASE PATH. The CMS is served at `/cms/` in the single-container deployment
 * and at `/` on its standalone host, so the worker URL and its scope both come
 * from `import.meta.env.BASE_URL`. The service worker resolves alert deep links
 * against that same scope - see src/client/public/sw.js.
 */

/** What the opt-in control renders from. */
export type StaffPushState =
  /** No service worker / Push API / Notification API (older Safari, some in-app browsers). */
  | "unsupported"
  /** The deployment has no VAPID identity - subscribing could never ring. */
  | "unconfigured"
  /** The user (or a policy) blocked notifications for this origin: sticky. */
  | "denied"
  /** Supported and permitted, but this browser is not subscribed. */
  | "off"
  /** This browser is subscribed and registered with the server. */
  | "on"
  /** Reading state or mid-transition. */
  | "loading";

export interface UseStaffPush {
  state: StaffPushState;
  /** True while enable()/disable() is in flight. */
  busy: boolean;
  /** The last failure, for the surface to show verbatim. */
  error: string | null;
  /** How many OTHER browsers this operator has opted in (this one excluded). */
  otherDevices: number;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

const SW_URL = `${import.meta.env.BASE_URL}sw.js`;
const SW_SCOPE = import.meta.env.BASE_URL;

function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * The VAPID public key arrives base64url; `subscribe` wants raw bytes. Browsers
 * accept a base64url string in some versions and a BufferSource in all of them,
 * so we always hand over bytes.
 */
export function vapidKeyToBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Backed by a fresh ArrayBuffer, not ArrayBufferLike: `applicationServerKey`
  // takes a BufferSource, and a view over a possibly-shared buffer does not
  // satisfy it. Same shape as the `new Uint8Array(body)` note in webPush.ts.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** A human-ish name for this browser, so an operator can tell two apart in the
 *  list. Never parsed - it is a label, not a capability signal. */
function describeBrowser(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /Mac OS X/.test(ua)
      ? "macOS"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad/.test(ua)
          ? "iOS"
          : "";
  return os ? `${browser} on ${os}` : browser;
}

export function useStaffPush(): UseStaffPush {
  const [state, setState] = useState<StaffPushState>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherDevices, setOtherDevices] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** Read-only: never registers a worker and never prompts. */
  const refresh = useCallback(async () => {
    if (!pushSupported()) {
      if (alive.current) setState("unsupported");
      return;
    }
    try {
      const { configured, devices } = await foyerApi.staffPushDevices();
      if (!alive.current) return;
      // An existing registration is only read, never created here.
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (!alive.current) return;

      const subscribedHere =
        !!sub && devices.length > 0 && Notification.permission === "granted";
      setOtherDevices(Math.max(0, devices.length - (subscribedHere ? 1 : 0)));

      if (!configured) {
        setState("unconfigured");
        return;
      }
      if (Notification.permission === "denied") {
        setState("denied");
        return;
      }
      setState(sub && Notification.permission === "granted" ? "on" : "off");
    } catch (e) {
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : "Could not read push status.");
      setState("off");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** MUST be called from a user gesture - see the module note. */
  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { publicKey, configured } = await foyerApi.staffPushKey();
      if (!configured || !publicKey) {
        setState("unconfigured");
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "off");
        return;
      }
      const reg = await navigator.serviceWorker.register(SW_URL, {
        scope: SW_SCOPE,
      });
      // `ready` rather than the register() result: on a first install the
      // worker is not active yet, and subscribing against an installing worker
      // throws in Firefox.
      await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub =
        existing ??
        (await reg.pushManager.subscribe({
          // Required by every browser that implements Push - a subscription
          // that could be pushed to silently is not offered any more.
          userVisibleOnly: true,
          applicationServerKey: vapidKeyToBytes(publicKey),
        }));
      await foyerApi.staffPushSubscribe({
        subscription: sub.toJSON(),
        label: describeBrowser(),
      });
      if (alive.current) setState("on");
    } catch (e) {
      if (alive.current) {
        setError(e instanceof Error ? e.message : "Could not enable push.");
      }
    } finally {
      if (alive.current) setBusy(false);
      await refresh();
    }
  }, [refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        // Tell the server FIRST. If the browser-side unsubscribe succeeded and
        // the server call then failed, we would keep pushing at an endpoint
        // that no longer exists until the 410s pruned it - noisy, and the
        // operator would see themselves as still subscribed.
        await foyerApi.staffPushUnsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      if (alive.current) setState("off");
    } catch (e) {
      if (alive.current) {
        setError(e instanceof Error ? e.message : "Could not turn push off.");
      }
    } finally {
      if (alive.current) setBusy(false);
      await refresh();
    }
  }, [refresh]);

  return { state, busy, error, otherDevices, enable, disable };
}
